/**
 * get-started — localStorage with TTL + cross-tab sync
 */
window.GetStartedStorage = (() => {
  const NS = 'get-started.';
  const SCHEMA = 'v1';
  const DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  const hasLS = (() => {
    try {
      localStorage.setItem('__getStartedT', '1');
      localStorage.removeItem('__getStartedT');
      return true;
    } catch {
      return false;
    }
  })();

  const listeners = new Map();
  const now = () => Date.now();
  const keyOf = (k) => `${NS}${k}`;

  const pack = (value, ttl = DEFAULT_TTL_MS) =>
    JSON.stringify({
      schema: SCHEMA,
      savedAt: now(),
      expiresAt: now() + ttl,
      value,
    });

  const parse = (raw) => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const valid = (obj) =>
    obj && obj.schema === SCHEMA && (!obj.expiresAt || obj.expiresAt > now());

  function load(k) {
    if (!hasLS) return null;
    const raw = localStorage.getItem(keyOf(k));
    const data = parse(raw);
    if (!valid(data)) {
      try {
        localStorage.removeItem(keyOf(k));
      } catch {}
      return null;
    }
    return data.value;
  }

  const queue = new Map();

  function save(k, value, { ttlMs = DEFAULT_TTL_MS, throttleMs = 120 } = {}) {
    if (!hasLS) return;
    if (!queue.has(k)) {
      queue.set(k, { last: value });
      setTimeout(() => {
        try {
          const last = queue.get(k).last;
          localStorage.setItem(keyOf(k), pack(last, ttlMs));
          emit(k, last);
        } catch {}
        queue.delete(k);
      }, throttleMs);
    } else {
      queue.get(k).last = value;
    }
  }

  function clear(k) {
    if (!hasLS) return;
    try {
      localStorage.removeItem(keyOf(k));
    } catch {}
    emit(k, null);
  }

  function onChange(k, cb) {
    if (!listeners.has(k)) listeners.set(k, new Set());
    listeners.get(k).add(cb);
    return () => listeners.get(k)?.delete(cb);
  }

  /**
   * @param {string} k
   * @param {*} v
   * @param {{ remote?: boolean }} [meta] remote=true when fired from window "storage" (other tab)
   */
  function emit(k, v, meta = {}) {
    const set = listeners.get(k);
    if (!set) return;
    set.forEach((fn) => {
      try {
        fn(v, meta);
      } catch {}
    });
  }

  if (hasLS) {
    window.addEventListener('storage', (e) => {
      if (!e.key || !e.key.startsWith(NS)) return;
      const shortKey = e.key.replace(NS, '');
      if (e.newValue == null) return emit(shortKey, null, { remote: true });
      const data = parse(e.newValue);
      emit(shortKey, valid(data) ? data.value : null, { remote: true });
    });
  }

  (function sweep() {
    if (!hasLS) return;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(NS)) continue;
      const data = parse(localStorage.getItem(k));
      if (!valid(data)) {
        try {
          localStorage.removeItem(k);
        } catch {}
      }
    }
  })();

  return { load, save, clear, onChange, DEFAULT_TTL_MS };
})();
